import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeft, ClipboardCheck } from "lucide-react"
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router"

import { api } from "@/api/cliente"
import { Calificacion } from "@/componentes/evaluaciones/Calificacion"
import { Consolidado, PlanDesarrollo } from "@/componentes/evaluaciones/Consolidado"
import {
  type Evaluacion,
  type EvaluacionResumen,
  type Notas,
  nota,
} from "@/componentes/evaluaciones/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha } from "@/lib/fechas"

const PREGUNTAS: { clave: string; texto: string }[] = [
  { clave: "fortalezas", texto: "¿Cuáles son tus fortalezas en el período?" },
  { clave: "mejorar", texto: "¿Qué debes mejorar?" },
  {
    clave: "obstaculos",
    texto: "¿Qué obstáculos encontraste (procesos, herramientas, información)?",
  },
  { clave: "metas", texto: "¿Qué metas te pones para el próximo trimestre?" },
]

/**
 * Mis evaluaciones (28/10): la puerta del trabajador al programa de evaluación.
 *
 * ⚠ **Mientras está abierta, sólo lo suyo**: la calificación de la Dirección no se le
 * enseña antes de la sesión (la decide el servidor, `evaluaciones.para_trabajador`). Ya
 * cerrada, ve el consolidado, su PDI y deja sus observaciones.
 */
export function MisEvaluaciones() {
  const { id } = useParams()
  return id ? <MiEvaluacion id={id} /> : <Lista />
}

function Lista() {
  const consulta = useQuery({
    queryKey: ["mis-evaluaciones"],
    queryFn: () => api.get<EvaluacionResumen[]>("/api/v1/mis-evaluaciones"),
  })
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <ClipboardCheck size={20} aria-hidden={true} /> Mis evaluaciones
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
          Tu evaluación del desempeño: haz tu autoevaluación con franqueza; el propósito es el
          desarrollo, no la sanción.
        </p>
      </header>
      {consulta.isPending && <EstadoCarga />}
      {consulta.isError && (
        <EstadoError
          titulo="No se pudieron cargar"
          error={consulta.error}
          onReintentar={() => consulta.refetch()}
        />
      )}
      {consulta.data?.length === 0 && (
        <p className="tarjeta p-4 text-sm" style={{ color: "var(--fg-muted)" }}>
          No tienes evaluaciones abiertas. Te avisará la Dirección cuando empiece el próximo
          período.
        </p>
      )}
      <ul className="space-y-2">
        {consulta.data?.map((e) => (
          <li key={e.id}>
            <Link
              to={`/mis-evaluaciones/${e.id}`}
              className="tarjeta-interactiva foco-cristal flex flex-wrap items-center justify-between gap-2 p-4"
            >
              <span>
                <span className="font-medium tabular-nums">
                  {comoFecha(e.desde)} al {comoFecha(e.hasta)}
                </span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {e.estado === "ABIERTA"
                    ? e.auto_enviada_en
                      ? "Autoevaluación entregada: espera la sesión 1 a 1"
                      : "Te toca: haz tu autoevaluación"
                    : e.estado === "CERRADA"
                      ? `Nota final ${nota(e.final)} · ${e.nivel}`
                      : "En sesión con la Dirección"}
                </span>
              </span>
              <Insignia
                tono={e.estado === "CERRADA" ? "exito" : e.auto_enviada_en ? "info" : "aviso"}
              >
                {e.estado === "CERRADA" ? "Cerrada" : e.auto_enviada_en ? "Entregada" : "Pendiente"}
              </Insignia>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

function MiEvaluacion({ id }: { id: string }) {
  const clienteQuery = useQueryClient()
  const [notas, setNotas] = useState<Notas>({})
  const [texto, setTexto] = useState<Record<string, string>>({})
  const [observacion, setObservacion] = useState("")
  const consulta = useQuery({
    queryKey: ["mi-evaluacion", id],
    queryFn: () => api.get<Evaluacion>(`/api/v1/mis-evaluaciones/${id}`),
  })
  useEffect(() => {
    if (consulta.data) {
      setNotas(consulta.data.notas_auto)
      setTexto(consulta.data.autoevaluacion)
      setObservacion(consulta.data.observaciones_trabajador ?? "")
    }
  }, [consulta.data])
  const listo = (ev: Evaluacion) => {
    clienteQuery.setQueryData(["mi-evaluacion", id], ev)
    clienteQuery.invalidateQueries({ queryKey: ["mis-evaluaciones"] })
  }
  const guardar = useMutation({
    mutationFn: () =>
      api.put<Evaluacion>(`/api/v1/mis-evaluaciones/${id}/autoevaluacion`, { notas, texto }),
    onSuccess: listo,
  })
  const entregar = useMutation({
    mutationFn: async () => {
      await api.put(`/api/v1/mis-evaluaciones/${id}/autoevaluacion`, { notas, texto })
      return api.post<Evaluacion>(`/api/v1/mis-evaluaciones/${id}/enviar`, {})
    },
    onSuccess: listo,
  })
  const observar = useMutation({
    mutationFn: () =>
      api.post<Evaluacion>(`/api/v1/mis-evaluaciones/${id}/observaciones`, { texto: observacion }),
    onSuccess: listo,
  })

  const ev = consulta.data
  if (consulta.isPending) return <EstadoCarga />
  if (consulta.isError || !ev) {
    return (
      <EstadoError
        titulo="No se pudo cargar"
        error={consulta.error}
        onReintentar={() => consulta.refetch()}
      />
    )
  }
  const editable = ev.estado === "ABIERTA" && ev.auto_enviada_en === null
  const error = guardar.error ?? entregar.error ?? observar.error

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <Link to="/mis-evaluaciones" className="foco-cristal inline-flex items-center gap-1 text-sm">
        <ArrowLeft size={16} /> Mis evaluaciones
      </Link>
      <header>
        <h1 className="text-xl font-semibold">Autoevaluación del desempeño</h1>
        <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
          {ev.plantilla.nombre} · {comoFecha(ev.desde)} al {comoFecha(ev.hasta)}
        </p>
      </header>
      {error && (
        <p role="alert" className="tarjeta p-3 text-sm" style={{ color: "var(--destructive)" }}>
          {(error as Error).message}
        </p>
      )}
      {!editable && ev.estado === "ABIERTA" && (
        <p className="tarjeta p-3 text-sm">
          Ya entregaste tu autoevaluación. La conversarán en la sesión 1 a 1.
        </p>
      )}

      <Calificacion
        prefijo="mia"
        formulario={ev.plantilla}
        notas={notas}
        onNotas={setNotas}
        soloLectura={!editable}
      />

      <section className="tarjeta space-y-3 p-4">
        {PREGUNTAS.map((p) => (
          <label key={p.clave} className="block text-sm">
            {p.texto}
            <textarea
              className="campo mt-1"
              rows={2}
              disabled={!editable}
              value={texto[p.clave] ?? ""}
              onChange={(e) => setTexto({ ...texto, [p.clave]: e.target.value })}
            />
          </label>
        ))}
      </section>

      {editable && (
        <div className="flex flex-wrap gap-2">
          <Boton disabled={guardar.isPending} onClick={() => guardar.mutate()}>
            {guardar.isPending ? "Guardando…" : "Guardar borrador"}
          </Boton>
          <Boton
            variante="primario"
            disabled={entregar.isPending}
            onClick={() => entregar.mutate()}
          >
            Entregar a la Dirección
          </Boton>
        </div>
      )}

      {ev.estado === "CERRADA" && ev.resultado && (
        <>
          <section className="space-y-2">
            <h2 className="text-base font-semibold">Tu resultado</h2>
            <Consolidado r={ev.resultado} />
          </section>
          <section className="space-y-2">
            <h2 className="text-base font-semibold">Tu Plan de Desarrollo Individual</h2>
            <PlanDesarrollo pdi={ev.pdi} />
          </section>
          <section className="space-y-2">
            <h2 className="text-base font-semibold">Tus observaciones</h2>
            <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Deja constancia de tu acuerdo o de cualquier desacuerdo con la evaluación.
            </p>
            <textarea
              className="campo"
              rows={3}
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
            />
            <Boton disabled={observar.isPending} onClick={() => observar.mutate()}>
              Guardar observaciones
            </Boton>
          </section>
        </>
      )}
    </div>
  )
}
